/**
 * Phase 326o — Platform Email Health panel.
 *
 * Mounted on the Super Admin dashboard. Shows three things at a glance:
 *   1. Resend API key status (configured / missing, last 4 chars masked).
 *   2. Last-24h send stats — % sent, per-type breakdown.
 *   3. Last 5 failures (subject, recipient, error snippet) for quick triage.
 *
 * Plus a one-click "Send test email to me" button that exercises the same
 * platform-level Resend path used by password resets + owner welcomes.
 *
 * Fails silently if the endpoint 403/errors so the rest of the dashboard
 * keeps working.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Mail, CheckCircle2, AlertTriangle, KeyRound, Send, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function PlatformEmailHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const r = await api.get('/super-admin/email-health');
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.status || 'unknown');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await api.post('/super-admin/email-health/test-send', {});
      if (r.data?.success) {
        toast.success(`Test email sent to ${r.data.to_email}`);
        // Re-poll the rollup so the new success row appears in totals.
        load({ silent: true });
      } else {
        toast.error(`Test failed: ${r.data?.error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="platform-email-health-loading">
        <CardContent className="p-5">
          <Skeleton className="h-6 w-56 mb-4" />
          <Skeleton className="h-24 rounded-xl" />
        </CardContent>
      </Card>
    );
  }
  if (error || !data) return null;

  const { resend, totals, by_type: byType = [], recent_failures: failures = [], window } = data;
  const pctSent = totals?.sent_pct ?? 100;
  const isHealthy = (totals?.total || 0) === 0 ? resend?.api_key_configured : pctSent >= 95;

  return (
    <Card
      className={`border-2 ${isHealthy ? 'border-emerald-200' : 'border-red-200'} bg-gradient-to-br ${isHealthy ? 'from-emerald-50/40' : 'from-red-50/40'} to-white`}
      data-testid="platform-email-health-panel"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Mail className={`h-4 w-4 ${isHealthy ? 'text-emerald-600' : 'text-red-600'}`} />
            Platform Email Health
          </span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] tracking-wider">
              {window?.hours ?? 24}H ROLLUP
            </Badge>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => load({ silent: true })}
              disabled={refreshing}
              className="h-7 w-7"
              data-testid="email-health-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">

        <div className="grid lg:grid-cols-2 gap-3">
          {/* ─── Resend key status ─── */}
          <div
            className={`p-3 rounded-xl bg-white border ${resend?.api_key_configured ? 'border-emerald-200' : 'border-red-300'}`}
            data-testid="email-health-key-status"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Resend API Key
              </span>
              {resend?.api_key_configured
                ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">CONFIGURED</Badge>
                : <Badge variant="destructive" className="text-[10px]">MISSING</Badge>}
            </div>
            <div className="font-mono text-xs text-slate-700 tabular-nums">
              {resend?.api_key_masked || <span className="text-red-600 italic">not set</span>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              From: <span className="font-mono">{resend?.from_email || '—'}</span>
            </p>
            {!resend?.api_key_configured && (
              <p className="text-[11px] text-red-700 mt-2 leading-relaxed">
                ⚠️ Password resets, owner welcome emails, and platform notifications cannot be sent.
                Configure via Settings → Email.
              </p>
            )}
          </div>

          {/* ─── 24h totals ─── */}
          <div
            className="p-3 rounded-xl bg-white border border-slate-200"
            data-testid="email-health-totals"
          >
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs text-muted-foreground">Last {window?.hours ?? 24}h sent rate</span>
              <span className={`text-2xl font-display font-semibold tabular-nums ${isHealthy ? 'text-emerald-700' : 'text-red-700'}`}>
                {pctSent}%
              </span>
            </div>
            <Progress
              value={pctSent}
              className="h-1.5 mb-2"
            />
            <div className="grid grid-cols-3 gap-2 text-center mt-2">
              <div data-testid="email-health-sent-count">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Sent</p>
                <p className="text-base font-display font-semibold tabular-nums text-emerald-700">{totals?.sent ?? 0}</p>
              </div>
              <div data-testid="email-health-failed-count">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Failed</p>
                <p className="text-base font-display font-semibold tabular-nums text-amber-700">{totals?.failed ?? 0}</p>
              </div>
              <div data-testid="email-health-error-count">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Error</p>
                <p className="text-base font-display font-semibold tabular-nums text-red-700">{totals?.error ?? 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Per-type breakdown ─── */}
        {byType.length > 0 && (
          <details className="bg-white rounded-lg border p-2.5" data-testid="email-health-by-type">
            <summary className="cursor-pointer text-xs font-medium text-slate-700">
              Per-type breakdown ({byType.length})
            </summary>
            <ul className="mt-2 space-y-1">
              {byType.map((b) => (
                <li
                  key={b.type}
                  className="flex items-center justify-between text-xs py-1 border-t first:border-t-0"
                  data-testid={`email-health-type-${b.type}`}
                >
                  <span className="font-mono text-slate-600">{b.type}</span>
                  <span className="flex items-center gap-2 tabular-nums">
                    <span className="text-emerald-700">✓{b.sent}</span>
                    {(b.failed || 0) > 0 && <span className="text-amber-700">⚠{b.failed}</span>}
                    {(b.error || 0) > 0 && <span className="text-red-700">✕{b.error}</span>}
                    <span className="text-slate-400">/ {b.total}</span>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* ─── Recent failures ─── */}
        {failures.length > 0 && (
          <details className="bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="email-health-failures">
            <summary className="cursor-pointer text-xs font-medium text-red-800 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              {failures.length} recent failure{failures.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-2 space-y-2">
              {failures.map((f, idx) => (
                <li key={idx} className="text-xs bg-white rounded p-2 border border-red-100">
                  <div className="flex items-center justify-between text-[11px] text-red-900 font-semibold">
                    <span>{f.type || 'unknown'}</span>
                    <span className="text-red-600">{f.to}</span>
                  </div>
                  <p className="text-[11px] text-slate-700 mt-0.5 truncate">{f.subject || '—'}</p>
                  {f.error && (
                    <code className="text-[10px] text-red-700 block mt-1 truncate">{f.error}</code>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* ─── Empty state when no recent traffic ─── */}
        {(totals?.total || 0) === 0 && (
          <div className="text-xs text-muted-foreground italic text-center py-2">
            No platform emails in the last {window?.hours ?? 24}h.
            {resend?.api_key_configured
              ? ' Hit "Send test email" to verify the pipeline.'
              : ' Configure Resend before sending anything.'}
          </div>
        )}

        {/* ─── Test send action ─── */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            onClick={sendTest}
            disabled={sendingTest || !resend?.api_key_configured}
            className="flex-1"
            data-testid="email-health-test-send-btn"
          >
            {sendingTest
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Send className="h-4 w-4 mr-2" />}
            {sendingTest ? 'Sending…' : 'Send test email to me'}
          </Button>
          {isHealthy && resend?.api_key_configured && (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          )}
        </div>

      </CardContent>
    </Card>
  );
}
